import React, { useState, useRef } from 'react';
import { useData, DOCTOR_INFO } from '../context/DataContext';

const ReceptionistPanel = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('slider');
  const { news, sliderImages, addNews, updateNews, deleteNews, addSliderImage, updateSliderImage, deleteSliderImage } = useData();

  const tabs = [
    { id: 'slider', label: 'Slider Images', icon: '🖼️' },
    { id: 'news', label: 'News & Offers', icon: '📢' },
    { id: 'info', label: 'Clinic Info', icon: 'ℹ️' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="Logo" className="w-10 h-10" />
            <div>
              <h1 className="font-bold text-gray-900">Receptionist Panel</h1>
              <p className="text-xs text-gray-500">Website Management</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">👩‍💼 {user.name}</span>
            <button onClick={onLogout} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
              <span>{tab.icon}</span> {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'slider' && <SliderManager sliderImages={sliderImages} addSliderImage={addSliderImage} updateSliderImage={updateSliderImage} deleteSliderImage={deleteSliderImage} />}
        {activeTab === 'news' && <NewsManager news={news} addNews={addNews} updateNews={updateNews} deleteNews={deleteNews} />}
        {activeTab === 'info' && <ClinicInfoView />}
      </div>
    </div>
  );
};

// Slider Manager with Device Upload
const SliderManager = ({ sliderImages, addSliderImage, updateSliderImage, deleteSliderImage }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ imageUrl: '', title: '', description: '', active: true });
  const [uploadType, setUploadType] = useState('url'); // 'url' or 'device'
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 2MB for localStorage)
      if (file.size > 2 * 1024 * 1024) {
        alert('Image size should be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm({ ...form, imageUrl: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.imageUrl) {
      alert('Please add an image');
      return;
    }
    if (editingId) {
      updateSliderImage(editingId, form);
    } else {
      addSliderImage(form);
    }
    resetForm();
  };

  const resetForm = () => {
    setForm({ imageUrl: '', title: '', description: '', active: true });
    setEditingId(null);
    setShowForm(false);
    setUploadType('url');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEdit = (image) => {
    setForm(image);
    setEditingId(image.id);
    setShowForm(true);
    setUploadType(image.imageUrl.startsWith('data:') ? 'device' : 'url');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">Homepage Slider Images</h2>
        <button onClick={() => setShowForm(true)} className="btn-primary text-sm">+ Add Image</button>
      </div>

      {showForm && (
        <div className="card p-6">
          <h3 className="font-bold text-gray-900 mb-4">{editingId ? 'Edit' : 'Add'} Slider Image</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Upload Type Selection */}
            <div className="flex gap-4 mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="uploadType" checked={uploadType === 'url'} onChange={() => setUploadType('url')} className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium">Image URL</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="uploadType" checked={uploadType === 'device'} onChange={() => setUploadType('device')} className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium">Upload from Device</span>
              </label>
            </div>

            {uploadType === 'url' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                <input type="url" value={form.imageUrl.startsWith('data:') ? '' : form.imageUrl} 
                  onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} 
                  className="input-field" placeholder="https://example.com/image.jpg" />
                <p className="text-xs text-gray-500 mt-1">Paste image URL from Unsplash, Pexels, or any website</p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Image</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                  <input type="file" ref={fileInputRef} accept="image/*" onChange={handleFileUpload} className="hidden" id="imageUpload" />
                  <label htmlFor="imageUpload" className="cursor-pointer">
                    <div className="text-4xl mb-2">📷</div>
                    <p className="text-sm text-gray-600">Click to select image</p>
                    <p className="text-xs text-gray-400 mt-1">Max size: 2MB (JPG, PNG, GIF)</p>
                  </label>
                </div>
              </div>
            )}

            {/* Image Preview */}
            {form.imageUrl && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Preview</label>
                <div className="relative w-full h-40 bg-gray-100 rounded-lg overflow-hidden">
                  <img src={form.imageUrl} alt="Preview" className="w-full h-full object-cover" 
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Invalid+Image'; }} />
                  <button type="button" onClick={() => setForm({ ...form, imageUrl: '' })} 
                    className="absolute top-2 right-2 bg-red-500 text-white w-6 h-6 rounded-full text-xs hover:bg-red-600">×</button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-field" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-field" required />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input type="checkbox" id="active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4" />
              <label htmlFor="active" className="text-sm text-gray-700">Active (Show on website)</label>
            </div>
            
            <div className="flex gap-2">
              <button type="submit" className="btn-primary text-sm">{editingId ? 'Update' : 'Add'} Image</button>
              <button type="button" onClick={resetForm} className="btn-secondary text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        {sliderImages.map((image) => (
          <div key={image.id} className={`card overflow-hidden ${image.active ? 'border-2 border-green-400' : 'opacity-60'}`}>
            <div className="h-40 bg-gray-200 relative">
              <img src={image.imageUrl} alt={image.title} className="w-full h-full object-cover" 
                onError={(e) => { e.target.src = 'https://via.placeholder.com/400x200?text=Image+Error'; }} />
              {image.active && <span className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">Active</span>}
            </div>
            <div className="p-4">
              <h4 className="font-bold text-gray-900">{image.title}</h4>
              <p className="text-sm text-gray-500">{image.description}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => handleEdit(image)} className="text-xs px-3 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200">Edit</button>
                <button onClick={() => updateSliderImage(image.id, { active: !image.active })} className="text-xs px-3 py-1 bg-yellow-100 text-yellow-600 rounded hover:bg-yellow-200">
                  {image.active ? 'Hide' : 'Show'}
                </button>
                <button onClick={() => { if (window.confirm('Delete?')) deleteSliderImage(image.id); }} className="text-xs px-3 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {sliderImages.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <div className="text-5xl mb-4">🖼️</div>
          <p>No slider images yet. Add your first image!</p>
        </div>
      )}
    </div>
  );
};

// News Manager
const NewsManager = ({ news, addNews, updateNews, deleteNews }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', active: true });

  const handleSubmit = (e) => {
    e.preventDefault();
    editingId ? updateNews(editingId, form) : addNews(form);
    resetForm();
  };

  const resetForm = () => {
    setForm({ title: '', content: '', active: true });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (item) => {
    setForm({ title: item.title, content: item.content, active: item.active });
    setEditingId(item.id);
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">News & Offers</h2>
        <button onClick={() => setShowForm(true)} className="btn-primary text-sm">+ Add News</button>
      </div>

      {/* Preview */}
      {news.filter(n => n.active).length > 0 && (
        <div className="bg-gradient-primary rounded-xl p-4">
          <p className="text-white/80 text-xs mb-2">📱 Website Preview:</p>
          <div className="bg-white/30 backdrop-blur-sm rounded-xl p-4 border border-white/20">
            <p className="text-blue-100 text-xs uppercase tracking-wider mb-1 flex items-center gap-2">
              <span>📢</span> LATEST NEWS
            </p>
            <h4 className="text-white font-bold">{news.filter(n => n.active)[0]?.title}</h4>
            <p className="text-white/80 text-sm mt-1">{news.filter(n => n.active)[0]?.content.substring(0, 80)}...</p>
          </div>
        </div>
      )}

      {showForm && (
        <div className="card p-6">
          <h3 className="font-bold text-gray-900 mb-4">{editingId ? 'Edit' : 'Add'} News</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-field" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
              <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="input-field" rows="4" required />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="newsActive" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4" />
              <label htmlFor="newsActive" className="text-sm text-gray-700">Active (Show on website)</label>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary text-sm">{editingId ? 'Update' : 'Add'}</button>
              <button type="button" onClick={resetForm} className="btn-secondary text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {news.map((item) => (
          <div key={item.id} className={`card p-5 ${item.active ? 'border-l-4 border-green-500' : 'border-l-4 border-gray-300 opacity-60'}`}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-bold text-gray-900">{item.title}</h4>
                  {item.active && <span className="bg-green-100 text-green-600 text-xs px-2 py-0.5 rounded">Active</span>}
                </div>
                <p className="text-gray-600 text-sm mb-2">{item.content}</p>
                <p className="text-xs text-gray-400">Posted: {item.date}</p>
              </div>
              <div className="flex gap-2 ml-4">
                <button onClick={() => handleEdit(item)} className="text-xs px-3 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200">Edit</button>
                <button onClick={() => updateNews(item.id, { active: !item.active })} className="text-xs px-3 py-1 bg-yellow-100 text-yellow-600 rounded hover:bg-yellow-200">
                  {item.active ? 'Hide' : 'Show'}
                </button>
                <button onClick={() => { if (window.confirm('Delete?')) deleteNews(item.id); }} className="text-xs px-3 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {news.length === 0 && <div className="text-center py-12 text-gray-500">No news items yet.</div>}
    </div>
  );
};

// Clinic Info View
const ClinicInfoView = () => (
  <div className="card p-6">
    <h2 className="text-xl font-bold text-gray-900 mb-6">Clinic Information</h2>
    <div className="grid md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div><label className="text-sm text-gray-500">Clinic Name</label><p className="font-semibold">{DOCTOR_INFO.clinicName}</p></div>
        <div><label className="text-sm text-gray-500">Doctor</label><p className="font-semibold">{DOCTOR_INFO.name}</p></div>
        <div><label className="text-sm text-gray-500">Qualifications</label><p className="font-semibold text-sm">{DOCTOR_INFO.degrees.join(', ')}</p></div>
        <div><label className="text-sm text-gray-500">PMDC</label><p className="font-semibold">{DOCTOR_INFO.pmdc}</p></div>
      </div>
      <div className="space-y-4">
        <div><label className="text-sm text-gray-500">Address</label><p className="font-semibold">{DOCTOR_INFO.address}</p></div>
        <div><label className="text-sm text-gray-500">Phone</label><p className="font-semibold">{DOCTOR_INFO.phone}</p></div>
        <div><label className="text-sm text-gray-500">OPD Timing</label><p className="font-semibold">{DOCTOR_INFO.opdTiming}</p></div>
        <div><label className="text-sm text-gray-500">Emergency</label><p className="font-semibold">{DOCTOR_INFO.emergency}</p></div>
      </div>
    </div>
  </div>
);

export default ReceptionistPanel;
